import { RadialGradient } from '@jouzen/ecom-components/v3/RadialGradient';
import { useLocale } from 'next-intl';

import { EventType } from '@/analytics/types';
import LandingPageHero from '@/app/components/LandingPage/LandingPageHero';
import getLocalePartialUrl from '@/utils/getLocalePartialUrl';

import useSAndR2025 from '../_hooks/useSAndR2025';

const BackgroundOverlay = () => (
  <div className="absolute top-0 size-full">
    <div className="absolute top-0 size-full lg:hidden">
      <RadialGradient
        className="scale-x-150"
        hex="#000000"
        id="mobile-gradient-1"
        opacity={0.65}
        position={[50, 0]}
        size={60}
        shape="circle"
      />
      <RadialGradient
        className="scale-x-150"
        hex="#321F16"
        id="mobile-gradient-2"
        opacity={0.65}
        position={[50, 100]}
        size={50}
        shape="circle"
      />
    </div>
    <div className="absolute top-0 size-full max-lg:hidden">
      <RadialGradient
        hex="#321F16"
        id="desktop-gradient-1"
        opacity={0.8}
        position={[20, 100]}
        size={40}
        shape="circle"
      />
      <RadialGradient
        hex="#0C0C0C"
        id="desktop-gradient-2"
        opacity={0.25}
        position={[0, 50]}
        size={60}
        shape="circle"
      />
      <RadialGradient
        hex="#0C0C0C"
        id="desktop-gradient-3"
        opacity={0.25}
        position={[10, 50]}
        size={60}
        shape="circle"
      />
    </div>
  </div>
);

const ScienceAndResearchHeroOR4 = () => {
  const isRolloutEnabled = useSAndR2025();
  const locale = useLocale();
  const localePartialUrl = getLocalePartialUrl(locale);

  const srcSet = isRolloutEnabled
    ? [
        {
          media: '(max-width: 767px)',
          src: '/blue-sky/science-and-research/science-research-hero-mobile.jpg?ar=1:1&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.6&fp-z=1.1',
        },
        {
          media: '(min-width: 768px) and (max-width: 1023px)',
          src: '/blue-sky/science-and-research/science-research-hero-mobile.jpg?ar=1:1&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.55&fp-z=1.3',
        },
        {
          media: '(min-width: 1024px)',
          src: '/blue-sky/science-and-research/science-research-hero-desktop.jpg?ar=5:4&fit=crop&crop=focalpoint&fp-x=0.75&fp-y=0.4&fp-z=0.8',
        },
      ]
    : [
        {
          media: '(max-width: 1023px)',
          src: '/blue-sky/science-and-research/science-research-hero-10-24.jpg?ar=1:1&fit=crop&crop=focalpoint&fp-x=0.6&fp-y=0.5&fp-z=1',
        },
        {
          media: '(min-width: 1024px)',
          src: '/blue-sky/science-and-research/science-research-hero-10-24.jpg?ar=5:4&fit=crop&crop=focalpoint&fp-x=0.43&fp-y=0.65&fp-z=1.1',
        },
      ];

  const handleClick = async (): Promise<void> => {
    const path = `https://ouraring.com/blog${localePartialUrl}oura-in-research/`;

    await window.ouraAnalytics.track(EventType.LinkClicked, {
      cta: 'view oura in research',
      location: 'body',
      path,
    });
    window.open(path, '_blank');
  };

  return (
    <LandingPageHero
      backgroundImage={{
        localizedAlt: 'science_and_research_hero_alt',
        srcSet,
      }}
      backgroundOverlay={isRolloutEnabled ? null : <BackgroundOverlay />}
      backgroundProps={{
        color: 'sandstone-400',
        gradients: [],
      }}
      copy={
        isRolloutEnabled
          ? 'science_research_2025_hero_subtitle'
          : 'science_and_research_hero_description'
      }
      cta={
        isRolloutEnabled
          ? {
              label: 'science_research_2025_hero_cta',
              onClick: handleClick,
              variant: 'secondary-light',
            }
          : null
      }
      data-cy="science-and-research-hero-or4"
      eyebrow="science_and_research_hero_eyebrow"
      title={
        isRolloutEnabled
          ? 'science_research_2025_hero_title'
          : 'science_and_research_hero_title'
      }
      textColor="text-sandstone-200"
    />
  );
};

export default ScienceAndResearchHeroOR4;
